import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Input, Select, Switch, InputNumber, message, Popconfirm, Space, Spin } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined } from "@ant-design/icons";
import { Send, Twitter, Wallet, Octagon, GripVertical, UserPlus, ListChecks } from "lucide-react";
import defaultTaskApi from "../../api/defaultTaskApi";
import "../../components/AddUserModal/style.css";

const { Option } = Select;

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const selectGlass =
    "w-full [&_.ant-select-selector]:!min-h-[40px] [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!border-white/10 [&_.ant-select-selector]:!bg-white/[0.06] [&_.ant-select-selection-item]:!text-white/90 [&_.ant-select-selection-placeholder]:!text-white/35 [&_.ant-select-arrow]:!text-white/45";

const inputGlass =
    "!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/90 hover:!border-white/[0.18] focus:!border-emerald-400/50";

const inputNumGlass =
    "w-full !rounded-xl [&_.ant-input-number-input]:!text-white/90 [&_.ant-input-number-handler-wrap]:!border-white/10";

const typeOptions = [
    { value: "telegram_subscribe", label: "Telegram Subscribe", icon: <Send size={14} /> },
    { value: "twitter_follow", label: "Twitter / X Follow", icon: <Twitter size={14} /> },
    { value: "wallet_connect", label: "Wallet Connect", icon: <Wallet size={14} /> },
    { value: "invite_friend", label: "Invite Friend", icon: <UserPlus size={14} /> },
    { value: "custom", label: "Custom", icon: <Octagon size={14} /> },
];

const iconOptions = [
    { value: "telegram", label: "Telegram" },
    { value: "twitter", label: "Twitter / X" },
    { value: "wallet", label: "Wallet" },
    { value: "invite", label: "Invite Friend" },
    { value: "octo", label: "Octo" },
];

const TYPE_BADGE_CLASS = {
    telegram_subscribe: "border-sky-400/35 bg-sky-500/12 text-sky-100",
    twitter_follow: "border-sky-500/35 bg-sky-600/12 text-sky-200",
    wallet_connect: "border-violet-400/35 bg-violet-500/12 text-violet-100",
    invite_friend: "border-amber-400/35 bg-amber-500/12 text-amber-100",
    custom: "border-white/20 bg-white/[0.08] text-white/65",
};

export default function DefaultTasksPage() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [form] = Form.useForm();

    // Ссылка нужна только для внешних действий (подписка/фоллоу/кастом).
    // «Пригласить друга» и «Подключить кошелёк» — внутренние механики, ссылки нет.
    const watchedType = Form.useWatch("type", form);
    const needsLink = !["invite_friend", "wallet_connect"].includes(watchedType);
    const linkPlaceholder =
        watchedType === "twitter_follow" ? "https://x.com/yourprofile" : "https://t.me/yourchannel";

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const data = await defaultTaskApi.getAll();
            setTasks(data);
        } catch (e) {
            message.error("Failed to load default tasks");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    const openCreate = () => {
        setEditingTask(null);
        form.resetFields();
        form.setFieldsValue({ is_active: true, sort_order: 0, is_locked: true });
        setModalOpen(true);
    };

    const openEdit = (task) => {
        setEditingTask(task);
        form.setFieldsValue(task);
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingTask) {
                await defaultTaskApi.update(editingTask.id, values);
                message.success("Task updated");
            } else {
                await defaultTaskApi.create(values);
                message.success("Task created");
            }
            setModalOpen(false);
            fetchTasks();
        } catch (e) {
            if (e.errorFields) return;
            message.error("Failed to save task");
        }
    };

    const handleDelete = async (id) => {
        try {
            await defaultTaskApi.remove(id);
            message.success("Task deleted");
            fetchTasks();
        } catch (e) {
            message.error("Failed to delete task");
        }
    };

    const handleToggle = async (task) => {
        try {
            await defaultTaskApi.update(task.id, { is_active: !task.is_active });
            fetchTasks();
        } catch (e) {
            message.error("Failed to toggle task");
        }
    };

    const getTypeBadge = (type) => {
        const opt = typeOptions.find((o) => o.value === type);
        const tone = TYPE_BADGE_CLASS[type] || TYPE_BADGE_CLASS.custom;
        return (
            <span
                className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-0.5 text-[11px] font-medium",
                    tone
                )}
            >
                {opt?.icon}
                {opt?.label || type || "—"}
            </span>
        );
    };

    if (loading && !tasks.length) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                </section>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-sky-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <ListChecks className="h-5 w-5 text-sky-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">Default Tasks</h1>
                            <p className="text-[12px] text-white/45">Preset tasks for promos: type, link, sort order</p>
                        </div>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreate}
                        className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                    >
                        Add Task
                    </Button>
                </div>
            </section>

            <section className={cn(shellClass, "relative flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading && tasks.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/45 backdrop-blur-sm">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                    {tasks.length === 0 ? (
                        <div className="flex flex-col items-center py-16 text-white/45">
                            <GripVertical size={48} strokeWidth={1.25} className="mb-3 opacity-35" />
                            <p className="text-[15px] font-medium text-white/55">No default tasks yet</p>
                            <p className="mt-1 max-w-sm text-center text-[13px] text-white/40">
                                Click &quot;Add Task&quot; to create your first default task.
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {tasks.map((task) => (
                                <div
                                    key={task.id}
                                    className={cn(
                                        "flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-sm transition-opacity sm:flex-row sm:items-center sm:gap-4",
                                        !task.is_active && "opacity-45"
                                    )}
                                >
                                    <span className="w-8 shrink-0 text-center font-mono text-[11px] text-emerald-300/90">
                                        #{task.id}
                                    </span>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="truncate text-[14px] font-medium text-white/90">
                                                {task.title}
                                            </span>
                                            {getTypeBadge(task.type)}
                                        </div>
                                        {task.link && (
                                            <a
                                                href={task.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-1 flex items-center gap-1 truncate text-[12px] text-sky-300/90 hover:text-sky-200"
                                            >
                                                <LinkOutlined style={{ fontSize: 10 }} />
                                                {task.link}
                                            </a>
                                        )}
                                    </div>

                                    <span className="shrink-0 text-[11px] text-white/40">order: {task.sort_order}</span>

                                    <Switch
                                        checked={task.is_active}
                                        onChange={() => handleToggle(task)}
                                        size="small"
                                        className="shrink-0 [&_.ant-switch-checked]:!bg-emerald-500"
                                    />

                                    <Space size={4} className="shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => openEdit(task)}
                                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-amber-200 transition-colors hover:border-white/20 hover:bg-white/10"
                                        >
                                            <EditOutlined style={{ fontSize: 13 }} />
                                        </button>
                                        <Popconfirm
                                            title="Delete this task?"
                                            onConfirm={() => handleDelete(task.id)}
                                            okText="Yes"
                                            cancelText="No"
                                            okButtonProps={{ className: "!rounded-lg" }}
                                            cancelButtonProps={{ className: "!rounded-lg" }}
                                        >
                                            <button
                                                type="button"
                                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-400/30 bg-rose-500/10 text-rose-300 transition-colors hover:border-rose-400/50 hover:bg-rose-500/20"
                                            >
                                                <DeleteOutlined style={{ fontSize: 13 }} />
                                            </button>
                                        </Popconfirm>
                                    </Space>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Modal
                title={editingTask ? "Edit Default Task" : "Create Default Task"}
                open={modalOpen}
                onOk={handleSubmit}
                onCancel={() => setModalOpen(false)}
                okText={editingTask ? "Save" : "Create"}
                className="custom-modal"
                destroyOnClose
                okButtonProps={{
                    className: "!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400",
                }}
                cancelButtonProps={{
                    className: "!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10",
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="title"
                        label={<span className="text-white/70">Title</span>}
                        rules={[{ required: true, message: "Title is required" }]}
                    >
                        <Input placeholder="e.g. Join us on TG" className={inputGlass} size="large" />
                    </Form.Item>
                    <Form.Item name="type" label={<span className="text-white/70">Type</span>}>
                        <Select
                            placeholder="Select type"
                            allowClear
                            className={selectGlass}
                            popupClassName="glass-ant-select-dropdown"
                            size="large"
                        >
                            {typeOptions.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {needsLink && (
                        <Form.Item name="link" label={<span className="text-white/70">Link</span>}>
                            <Input placeholder={linkPlaceholder} className={inputGlass} size="large" />
                        </Form.Item>
                    )}
                    <Form.Item name="icon" label={<span className="text-white/70">Icon</span>}>
                        <Select
                            placeholder="Select icon"
                            allowClear
                            className={selectGlass}
                            popupClassName="glass-ant-select-dropdown"
                            size="large"
                        >
                            {iconOptions.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="sort_order" label={<span className="text-white/70">Sort Order</span>}>
                        <InputNumber min={0} className={inputNumGlass} size="large" />
                    </Form.Item>
                    <Form.Item
                        name="is_active"
                        label={<span className="text-white/70">Active</span>}
                        valuePropName="checked"
                    >
                        <Switch className="[&_.ant-switch-checked]:!bg-emerald-500" />
                    </Form.Item>
                    <Form.Item
                        name="is_locked"
                        label={<span className="text-white/70">Обязательный (промоутер не может заменить)</span>}
                        valuePropName="checked"
                        extra={<span className="text-[11px] text-white/40">Выкл — промоутер может заменить этот таск своим кастомным в мини-аппке</span>}
                    >
                        <Switch className="[&_.ant-switch-checked]:!bg-emerald-500" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
